import React, { useEffect, useState } from 'react';
import './styles.scss';
import { ReactComponent as HomeIcon } from 'assets/icons/home.svg';
import { ReactComponent as MealsIcon } from 'assets/icons/meals.svg';
import { ReactComponent as FeedsIcon } from 'assets/icons/feed.svg';
import { ReactComponent as MetadataIcon } from 'assets/icons/metadata.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as StarIcon } from 'assets/icons/star-icon.svg';
import { ReactComponent as GiftIcon } from 'assets/icons/gift.svg';
import { ReactComponent as BriefCaseIcon } from 'assets/icons/briefcase.svg';
import { ReactComponent as QRCodeIcon } from 'assets/icons/qrcode.svg';

import { Link, useLocation } from 'react-router-dom';
import { SidebarItemType } from './types';
import { ROUTE_KEYS } from 'utils/constants';
import { metadataList, baseItemsList, rewardsList, giftcardList, mealItemsList } from './constants';
import CokitchenDropdown from './cokitchen-dropdown';
import useDashboardContext from 'contexts/dashboard.context';
import { getTrimmedPath } from 'utils/functions';
import { ENUM_CATEGORY, ENUM_COKITCHEN_STATUS } from 'utils/enum';
import { EditAltIcon } from 'cokitchen-icons';

const Sidebar = () => {
  const { setShowMobileSidebar, activeCokitchen, category, routePrefix } = useDashboardContext();
  const [activeMenuItem, setActiveMenuItem] = useState('');
  const location = useLocation();

  const SidebarMenu: SidebarItemType[] = [
    {
      name: 'Overview',
      icon: <HomeIcon />,
      route: `${routePrefix}/${ROUTE_KEYS.OVERVIEW}`
    },
    {
      name: 'Items by Category',
      icon: <MealsIcon />,
      route: `${routePrefix}/${ROUTE_KEYS.ITEMS_BY_CATEGORY}`,
      type: ENUM_CATEGORY.SHOP
    },

    {
      name: 'Meals',
      icon: <MealsIcon />,
      dropdownList: mealItemsList,
      type: ENUM_CATEGORY.FOOD
    },
    {
      name: 'Base Items',
      icon: <MetadataIcon />,
      dropdownList: baseItemsList,
      type: ENUM_CATEGORY.FOOD
    },
    {
      name: 'Feeds',
      icon: <FeedsIcon />,
      underline: true,
      route: `${ROUTE_KEYS.FEEDS}`
    },

    {
      name: 'Careers',
      icon: <BriefCaseIcon />,
      route: `${ROUTE_KEYS.CAREER}`
    },
    {
      name: 'Blog',
      icon: <EditAltIcon />,
      route: `${ROUTE_KEYS.BLOGS}`
    },
    {
      name: 'Manage Rewards',
      icon: <StarIcon />,
      dropdownList: rewardsList
    },
    {
      name: 'Manage Giftcards',
      icon: <GiftIcon />,
      dropdownList: giftcardList
    },
    {
      name: 'Metadata',
      icon: <MetadataIcon />,
      dropdownList: metadataList
    },
    {
      name: 'Settings',
      icon: <SettingsIcon />,
      route: `${ROUTE_KEYS.SETTINGS}`,
      underline: true
    },
    {
      name: 'Kitchen Details',
      icon: <HomeIcon />,
      route: `${routePrefix}/${ROUTE_KEYS.COKITCHEN_DETAILS}`
    },
    {
      name: 'Polygon',
      icon: <SettingsIcon />,
      route: `${routePrefix}/${ROUTE_KEYS.POLYGON}`
    },

    {
      name: 'Trash',
      route: `${ROUTE_KEYS.TRASH}`,
      icon: <TrashIcon />
    }
  ];

  if (activeCokitchen && activeCokitchen.cokitchen_type === ENUM_COKITCHEN_STATUS.DINE_IN) {
    SidebarMenu.splice(3, 0, {
      name: 'QR Code',
      icon: <QRCodeIcon />,
      route: `${ROUTE_KEYS.QR_CODE}`
    });
  }

  const handleDropdownClick = (label: string) => {
    setActiveMenuItem(activeMenuItem === label ? '' : label);
  };

  useEffect(() => {
    const currentRoute = getTrimmedPath();
    const listItem = SidebarMenu.find((i) => {
      const routes = i.route ? [i.route] : i.dropdownList?.map((d) => d.route) ?? [];
      return routes.includes(currentRoute);
    });
    if (listItem) {
      setActiveMenuItem(listItem.name);
    }
  }, []);

  const isLinkActive = (baseRoute: string) => {
    const pathname = window.location.pathname;
    // eslint-disable-next-line
    const [empty, path] = pathname.split('/');
    return pathname.startsWith(baseRoute);
  };

  const isDropdownActive = (dropdownItems?: any[]) => {
    const pathname = window.location.pathname;
    // eslint-disable-next-line
    const [empty, path] = pathname.split('/');
    return dropdownItems && dropdownItems[0].route.startsWith(path);
  };

  return (
    <div className='sidebar'>
      <div className='max-lg:hidden'>
        <CokitchenDropdown />
      </div>

      <div className='sidebar_bottom'>
        {SidebarMenu.filter((item) => !item.type || item.type === category).map(
          ({ name, icon, route, underline, dropdownList }, index) => (
            <div key={index}>
              {route ? (
                <Link
                  to={route}
                  onClick={() => setShowMobileSidebar(false)}
                  className={`sidebar_bottom_item ${isLinkActive(route) ? 'active' : 'inactive'}`}>
                  <div className='icon'>{icon}</div>
                  <p className='text-gray-6 font-medium text-base'>{name}</p>
                </Link>
              ) : (
                <div
                  onClick={() => {
                    handleDropdownClick(name);
                  }}
                  className={`sidebar_bottom_item ${
                    isDropdownActive(dropdownList) ? 'active' : 'inactive'
                  }`}>
                  <div className='icon'>{icon}</div>
                  <p className='text-gray-6 font-medium text-base'>{name}</p>
                </div>
              )}
              {activeMenuItem === name && (
                <div className='sidebar_bottom_item_dropdown'>
                  <ul>
                    {dropdownList?.map(({ name, route }, index) => (
                      <li
                        key={index}
                        className={`sidebar_bottom_dropdown_item ${
                          location.pathname === route ? 'is-active' : ''
                        }`}
                        onClick={() => setShowMobileSidebar(false)}>
                        <Link to={route}>{name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {underline && <hr className='border-[#DDDEDF] border-opacity-50 mb-4' />}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Sidebar;
